import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Check, UserRound, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const PARTICLES = Array.from({ length: 36 }, (_, i) => ({ a: (i / 36) * Math.PI * 2, d: 180 + ((i * 53) % 220), s: 3 + (i % 4) }));

export function StageUnlockOverlay({ stage, onEnter }) {
  // Hold the cinematic until no other modal (e.g. the daily Security-Pulse dialog) is open, so "Enter" is always clickable.
  const [clear, setClear] = useState(false);
  useEffect(() => {
    if (!stage) return undefined;
    const check = () => setClear(!document.querySelector('[data-testid="security-pulse-modal"], [role="dialog"][data-state="open"]'));
    check();
    const t = setInterval(check, 500);
    return () => clearInterval(t);
  }, [stage]);
  return (
    <AnimatePresence>
      {stage && clear && (
        <motion.div data-testid="vcb-stage-unlock" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[200] bg-[#040302] grid place-items-center overflow-hidden pointer-events-auto">
          <div className="absolute inset-0 pointer-events-none">
            {PARTICLES.map((p, i) => (
              <motion.span key={i} className="absolute left-1/2 top-1/2 rounded-full bg-[#D8B23E]" style={{ width: p.s, height: p.s }}
                           initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                           animate={{ x: Math.cos(p.a) * p.d, y: Math.sin(p.a) * p.d, opacity: [0, 1, 0], scale: [0, 1.4, 0.6] }}
                           transition={{ duration: 1.8, delay: 0.15 + (i % 6) * 0.05, ease: "easeOut" }} />
            ))}
            <motion.div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#D8B23E]/40"
                        initial={{ width: 0, height: 0, opacity: 0.9 }} animate={{ width: 900, height: 900, opacity: 0 }} transition={{ duration: 2, ease: "easeOut" }} />
          </div>
          <div className="relative text-center px-6 max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="font-grotesk text-[10px] uppercase tracking-[0.42em] text-[#8A6A1A]">Vinci Core Banking · Stage {stage.numeral}</motion.div>
            <motion.h1 initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.9, duration: 0.8 }} className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#D8B23E] mt-6 leading-[1.05]" data-testid="vcb-stage-unlock-title">{stage.title}</motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="font-serif text-lg md:text-xl text-[#E8DFC4] mt-6">Your privileges have been extended.</motion.p>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9 }} className="font-body text-[13px] text-[#8A8A82] mt-6 leading-relaxed max-w-2xl mx-auto">{stage.narrative}</motion.p>
            {stage.note && (
              <motion.blockquote initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.1 }} data-testid="vcb-stage-unlock-note"
                                 className="mt-7 mx-auto max-w-xl border-l-2 border-[#D8B23E] pl-5 text-left">
                <div className="font-grotesk text-[8px] uppercase tracking-[0.34em] text-[#D8B23E]">A personal note from the Vinci desk</div>
                <p className="font-serif italic text-[16px] md:text-lg text-[#F3EEE2] mt-2 leading-relaxed">“{stage.note}”</p>
                <div className="font-body text-[11px] text-[#A9A9A2] mt-2">— {stage.released_by || "Vinci desk"}</div>
              </motion.blockquote>
            )}
            <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.3 }} onClick={onEnter} data-testid="vcb-stage-unlock-enter"
                           className="mt-10 bg-[#D8B23E] hover:bg-[#E5C450] text-black font-grotesk text-[12px] uppercase tracking-[0.4em] px-14 py-4 shadow-[0_20px_60px_-20px_rgba(216,178,62,0.9)]">Enter</motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function StageTracker({ data, onSelect, selected }) {
  if (!data) return null;
  return (
    <div data-testid="vcb-stage-tracker" className="border border-[#D8B23E]/25 bg-[#070604] p-6">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.34em] text-[#8A6A1A]">Your Vinci Core journey</div>
      <div className="font-serif text-xl text-[#F5F5F0] mt-1">Stage {data.current_stage ? data.stages[data.current_stage - 1].numeral : "—"} of V {data.elite && <span className="text-[#D8B23E] text-sm ml-2">◆ Vinci Elite</span>}</div>
      <ol className="mt-6 relative ml-3">
        {data.stages.map((s, i) => {
          const active = s.status === "active";
          const current = s.is_current;
          return (
            <li key={s.n} className="relative pl-8 pb-6 last:pb-0">
              {i < data.stages.length - 1 && <span className={`absolute left-[5px] top-4 bottom-0 w-px ${active && data.stages[i + 1].status === "active" ? "bg-[#D8B23E]" : "bg-white/10"}`} />}
              <span className={`absolute left-0 top-1 h-[11px] w-[11px] rounded-full ${active ? "bg-[#D8B23E]" : "bg-[#2A2A2A] border border-white/15"} ${current ? "vcb-stage-pulse" : ""}`} />
              <button type="button" disabled={!active} onClick={() => onSelect?.(s)} data-testid={`vcb-tracker-stage-${s.n}`}
                      className={`text-left w-full ${active ? "hover:text-[#D8B23E]" : "cursor-default"}`}>
                <div className="flex items-center gap-2">
                  <span className={`font-grotesk text-[8px] uppercase tracking-[0.3em] ${active ? "text-[#8A6A1A]" : "text-[#55554F]"}`}>Stage {s.numeral}</span>
                  {active ? <Check size={10} className="text-[#D8B23E]" /> : <Lock size={9} className="text-[#D8B23E]/50" />}
                </div>
                <div className={`font-serif text-[15px] ${active ? "text-[#F5F5F0]" : "text-[#6A6A64]"} ${selected?.n === s.n ? "underline decoration-[#D8B23E]/60 underline-offset-4" : ""}`}>{s.title}</div>
              </button>
            </li>
          );
        })}
      </ol>
      {selected && (
        <div className="mt-6 border-t border-[#D8B23E]/20 pt-5" data-testid="vcb-tracker-detail">
          <div className="font-grotesk text-[8px] uppercase tracking-[0.3em] text-[#8A6A1A]">Unlocked at Stage {selected.numeral}</div>
          <p className="font-serif text-[14px] text-[#E8DFC4] mt-2 leading-relaxed">{selected.narrative}</p>
          {selected.note && <p className="font-serif italic text-[13px] text-[#D8B23E] mt-3 border-l border-[#D8B23E]/60 pl-3" data-testid="vcb-tracker-note">“{selected.note}” <span className="not-italic font-body text-[10px] text-[#A9A9A2]">— {selected.released_by}</span></p>}
          <ul className="mt-3 space-y-1">{selected.features.map((f) => <li key={f} className="font-body text-[12px] text-[#A9A9A2] flex gap-2"><span className="text-[#D8B23E]">◆</span>{f}</li>)}</ul>
        </div>
      )}
      <style>{`.vcb-stage-pulse{box-shadow:0 0 0 0 rgba(216,178,62,.7);animation:vcbPulse 1.8s infinite}@keyframes vcbPulse{0%{box-shadow:0 0 0 0 rgba(216,178,62,.7)}70%{box-shadow:0 0 0 10px rgba(216,178,62,0)}100%{box-shadow:0 0 0 0 rgba(216,178,62,0)}}`}</style>
    </div>
  );
}

export function RelationshipManagerCard({ rm, unread = 0 }) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [seen, setSeen] = useState(false);
  const endRef = useRef(null);
  const load = useCallback(() => api.get("/vcb/rm/messages").then((r) => { setMsgs(r.data.messages); setSeen(true); }).catch(() => {}), [api]);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (!open) return undefined; const t = setInterval(load, 20000); return () => clearInterval(t); }, [open, load]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { const r = await api.post("/vcb/rm/messages", { body: body.trim() }); setMsgs((m) => [...(m || []), r.data.message]); setBody(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };
  if (!rm?.name) return null;
  const badge = seen ? 0 : unread;
  return (
    <div data-testid="vcb-rm-card" className="border border-[#D8B23E]/40 bg-[#0A0906] p-6 mt-4">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.34em] text-[#D8B23E] inline-flex items-center gap-2"><UserRound size={11} /> Your relationship manager</div>
      <div className="font-serif text-xl text-[#F5F5F0] mt-2" data-testid="vcb-rm-name">{rm.name}</div>
      {rm.title && <div className="font-body text-[12px] text-[#A9A9A2] mt-0.5">{rm.title}</div>}
      <div className="mt-3 space-y-1 font-body text-[12px]">
        {rm.email && <a href={`mailto:${rm.email}`} className="block text-[#E8DFC4] hover:text-[#D8B23E]" data-testid="vcb-rm-email">{rm.email}</a>}
        {rm.phone && <a href={`tel:${rm.phone}`} className="block text-[#E8DFC4] hover:text-[#D8B23E]" data-testid="vcb-rm-phone">{rm.phone}</a>}
      </div>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="vcb-rm-message-toggle"
              className="mt-4 inline-flex items-center gap-2 border border-[#D8B23E]/60 text-[#D8B23E] px-4 py-2 font-grotesk text-[9px] uppercase tracking-[0.26em] hover:bg-[#D8B23E] hover:text-black">
        <MessageSquare size={11} /> {open ? "Hide thread" : "Message"}
        {badge > 0 && <span data-testid="vcb-rm-unread" className="ml-1 rounded-full bg-[#D8B23E] text-black text-[9px] font-bold px-1.5 py-0.5 leading-none">{badge}</span>}
      </button>
      {open && (
        <div className="mt-4 border-t border-[#D8B23E]/20 pt-4 pb-28" data-testid="vcb-rm-thread">
          <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
            {msgs === null && <div className="font-body text-[11px] text-[#8A8A82]">Loading…</div>}
            {msgs?.length === 0 && <div className="font-body text-[12px] text-[#8A8A82]">Begin the conversation — {rm.name} replies here and by e-mail.</div>}
            {msgs?.map((m) => (
              <div key={m.id} data-testid={`vcb-rm-msg-${m.from}`} className={`max-w-[92%] ${m.from === "customer" ? "ml-auto" : ""}`}>
                <div className={`px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${m.from === "customer" ? "bg-[#1A160A] text-[#F3EEE2] border border-[#D8B23E]/30" : "bg-[#0F0E0B] text-[#E8DFC4] border border-white/10"}`}>{m.body}</div>
                <div className={`font-body text-[9px] text-[#6A6A64] mt-1 ${m.from === "customer" ? "text-right" : ""}`}>{m.from === "customer" ? "You" : m.author} · {new Date(m.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="mt-3 flex gap-2">
            <textarea data-testid="vcb-rm-input" value={body} onChange={(e) => setBody(e.target.value)} rows={2} maxLength={2000} placeholder={`Write to ${rm.name}…`}
                      className="flex-1 bg-black/40 border border-white/15 text-[13px] text-[#F5F5F0] p-2 resize-none focus:border-[#D8B23E] outline-none" />
            <button type="button" onClick={send} disabled={busy || !body.trim()} data-testid="vcb-rm-send"
                    className="self-end bg-[#D8B23E] text-black px-4 py-2 font-grotesk text-[9px] uppercase tracking-[0.26em] hover:bg-[#E5C450] disabled:opacity-40">{busy ? "…" : "Send"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function useVCBStages() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(null);
  const load = useCallback(() => api.get("/vcb/stages/me").then((r) => { setData(r.data); setPending(r.data.pending_unlock || null); }).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  const enter = useCallback(async () => {
    if (!pending) return;
    try { await api.post("/vcb/stages/ack", { stage: pending.n }); } catch { /* ignore */ }
    setPending(null); load();
  }, [api, pending, load]);
  const current = useMemo(() => data?.stages?.find((s) => s.is_current) || null, [data]);
  return { data, pending, enter, current, reload: load };
}
